import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { btn } from "@/lib/ui";
import { OrderLines, Totals } from "@/components/shop/OrderLines";

const PAID = ["paid", "packed", "shipped", "delivered"];
const PENDING = ["pending_payment", "payment_failed"];

const Header = ({ order }) => {
  if (PAID.includes(order.status)) {
    return (
      <div className="text-center">
        <CheckCircle2 className="mx-auto h-14 w-14 text-brand-greenDark" aria-hidden="true" />
        <h1 className="mt-4 font-heading text-3xl font-extrabold text-brand-ink sm:text-4xl">Thank you for your order</h1>
        <p className="mt-2 text-base text-slate-700">Your payment was successful. A confirmation has been emailed to <strong>{order.customer.email}</strong>.</p>
      </div>
    );
  }
  if (order.status === "needs_attention") {
    return (
      <div className="text-center">
        <AlertTriangle className="mx-auto h-14 w-14 text-amber-700" aria-hidden="true" />
        <h1 className="mt-4 font-heading text-3xl font-extrabold text-brand-ink">We've received your payment</h1>
        <p className="mt-2 text-base text-slate-700">Our team needs to check stock for your order and will contact you shortly to arrange delivery or a full refund.</p>
      </div>
    );
  }
  return (
    <div className="text-center" data-testid="order-pending">
      <Clock className="mx-auto h-14 w-14 text-brand-blue" aria-hidden="true" />
      <h1 className="mt-4 font-heading text-3xl font-extrabold text-brand-ink">Confirming your payment…</h1>
      <p className="mt-2 text-base text-slate-700">This usually takes a few seconds. If money was deducted and this does not update, please contact us with your order number — please don't pay again.</p>
    </div>
  );
};

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const { settings } = useSite();
  const [order, setOrder] = useState(undefined);
  useSeo({ title: "Order confirmation | Young Spine Tricity", description: "Your order details.", path: "/order", noindex: true });

  const token = params.get("t") || "";
  const fetchOrder = useCallback(() => api.get(`/orders/${orderId}`, { params: { token } }).then((r) => r.data), [orderId, token]);

  useEffect(() => {
    let tries = 0;
    let timer;
    let alive = true;
    const poll = () =>
      fetchOrder()
        .then((data) => {
          if (!alive) return;
          setOrder(data);
          if (PENDING.includes(data.status) && tries++ < 15) timer = setTimeout(poll, 3000);
        })
        .catch(() => alive && setOrder(null));
    poll();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [fetchOrder, setOrder]);

  if (order === undefined) return <div className="container-x py-24"><div className="mx-auto h-64 max-w-2xl animate-pulse rounded-3xl bg-brand-tint" /></div>;
  if (order === null) return <div className="container-x py-24 text-center"><h1 className="text-2xl font-bold">Order not found</h1><Link to="/shop" className="text-link mt-4">Back to shop</Link></div>;
  const c = order.customer;
  return (
    <div className="bg-brand-tint py-12 md:py-20" data-testid="order-confirmation">
      <div className="container-x max-w-3xl">
        <Header order={order} />
        <div className="mt-10 space-y-6 rounded-3xl bg-white p-6 ring-1 ring-slate-200 sm:p-10">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-4">
            <p className="text-base text-slate-600">Order number</p>
            <p className="font-heading text-xl font-bold text-brand-ink" data-testid="order-number">{order.order_number}</p>
          </div>
          <OrderLines lines={order.items} />
          <Totals subtotal={order.subtotal} shipping={order.shipping} total={order.total} totalLabel={PAID.includes(order.status) ? "Amount paid" : "Order total"} />
          <div className="rounded-2xl bg-brand-tint p-5" data-testid="order-address">
            <p className="text-sm font-semibold uppercase tracking-wider text-brand-blue">Delivery address</p>
            <p className="mt-2 text-base text-slate-700">{c.name}<br />{c.line1}{c.line2 ? <><br />{c.line2}</> : null}<br />{c.city}, {c.state} {c.pincode}<br />India · {c.phone}</p>
          </div>
          <p className="text-base text-slate-700">{settings.delivery_text} We'll email you the courier name and tracking number when your order ships.</p>
          <p className="text-sm text-slate-600">Questions about your order? Call {settings.phone_display} or email {settings.email}.</p>
        </div>
        <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          <Link to="/" className={btn("outline", "md")} data-testid="order-home-btn">Back to home</Link>
          <Link to="/shop" className={btn("green", "md")} data-testid="order-shop-btn">Continue shopping</Link>
        </div>
      </div>
    </div>
  );
}
